import {
  HyperlinkStaticCellRenderer,
  IGridHyperlink,
} from '../../../utils/custom-renderer/hyperlink-cell-renderer';
import { CASCADE_ENUM } from '../../../services/enum.service';
import {
  convertDateToUTC,
  getFormattedDate,
  getFormattedNumber,
  getNumericPattern,
} from '../../../utils/formatting';
import { CONTRACT_TYPE, ICON_COLORS, ICONS, NEGOTIATION_STATUS } from '../../../utils';
import { getAbbreviationForCreditProfile } from '../../../utils/credit-profiles';
import {
  ActionCellRenderer,
  ActionTypes,
} from '../../../utils/custom-renderer/action-cell-renderer';
import {
  getGroupColumnSameObject,
  headerWithTooltipColParams,
  CUSTOM_HEADER_TOOLTIPS,
  compareVPPAValues,
} from '../../../utils/grid';

export enum COLUMN_FIELDS {
  RFQ_DETAILS = 'RFQ_DETAILS',
  VPPA_DETAILS = 'VPPA_DETAILS',
  UNIQUE_ID = 'UNIQUE_ID',
  BUYER_COMPANY_NAME = 'BUYER_COMPANY_NAME',
  SELLER_COMPANY_NAME = 'SELLER_COMPANY_NAME',
  TRADE_DETAILS = 'TRADE_DETAILS',
  TECHNOLOGY = 'TECHNOLOGY',
  PROJECT_NAME = 'NAME',
  ISO = 'ISO',
  SELLER_SETTLEMENT_POINT = 'SELLER_SETTLEMENT_POINT',
  OFFERED_CAPACITY = 'OFFERED_CAPACITY',
  FIXED_PRICE = 'FIXED_PRICE',
  TENOR_START_DATE = 'TENOR_START_DATE',
  TENOR_MONTHS = 'TENOR_MONTHS',
  APPLICABLE_REC_PROGRAM = 'APPLICABLE_REC_PROGRAM',
  BUYER_POST_COD_COLLATERAL = 'BUYER_POST_COD_COLLATERAL',
  SELLER_POST_COD_COLLATERAL = 'SELLER_POST_COD_COLLATERAL',
  NEGOTIATION_STATUS = 'NEGOTIATION_STATUS',
  PROJECT_ID = 'PROJECT_ID',
  EXECUTED_DATE_TIME = 'EXECUTED_DATE_TIME',
}

function getSettlementPoint(params) {
  return params.data
    ? params.data.VPPA_SETTLEMENT_POINT
    : getGroupColumnSameObject(params, COLUMN_FIELDS.VPPA_DETAILS).SETTLEMENT_POINT;
}

function getApplicableRecProgram(context, params) {
  return params.data
    ? context.enumService.getEnumTranslation(
        CASCADE_ENUM.APPLICABLE_REC_PROGRAM,
        params.data.APPLICABLE_REC_PROGRAM,
      )
    : context.enumService.getEnumTranslation(
        CASCADE_ENUM.APPLICABLE_REC_PROGRAM,
        getGroupColumnSameObject(params, COLUMN_FIELDS.VPPA_DETAILS).APPLICABLE_REC_PROGRAM,
      );
}

export function NEGOTIATIONS_COLUMNS(context: any) {
  return [
    {
      field: COLUMN_FIELDS.RFQ_DETAILS,
      headerName: 'RFQ Details',
      sortable: true,
      filter: 'agTextColumnFilter',
      lockPinned: true,
      menuTabs: ['filterMenuTab'],
      headerCheckboxSelection: false,
      flex: 1,
      cellRenderer: HyperlinkStaticCellRenderer,
      cellRendererParams: (params) => {
        const iconList: Array<{ icon: ICONS; width; height; fill? }> = [];
        if (params?.data?.RFQ_CONTRACT_TYPE === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED) {
          iconList.push({
            icon: ICONS.AUTO_EXECUTED_ICON,
            width: '16px',
            height: '16px',
            fill: ICON_COLORS.AUTO_EXECUTED_STATUS,
          });
        }
        const hyperlinkParams: IGridHyperlink = {
          value: params.value,
          id: '',
          vppaId: null,
          iconList: iconList,
        };

        return hyperlinkParams;
      },
    },
    {
      field: COLUMN_FIELDS.UNIQUE_ID,
      headerName: 'Transaction ID',
      sortable: true,
      filter: 'agTextColumnFilter',
      lockPinned: true,
      menuTabs: ['filterMenuTab'],
      headerCheckboxSelection: false,
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.VPPA_DETAILS,
      headerName: 'Contract Details',
      sortable: true,
      filter: 'agTextColumnFilter',
      lockPinned: true,
      menuTabs: ['filterMenuTab'],
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.BUYER_COMPANY_NAME,
      headerName: 'Buyer',
      suppressMenu: true,
      lockPinned: true,
      headerCheckboxSelection: false,
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.SELLER_COMPANY_NAME,
      headerName: 'Seller',
      sortable: true,
      filter: 'agTextColumnFilter',
      lockPinned: true,
      menuTabs: ['filterMenuTab'],
      headerCheckboxSelection: false,
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.TECHNOLOGY,
      headerName: 'Technology',
      sortable: true,
      filter: 'agSetColumnFilter',
      lockPinned: true,
      menuTabs: ['filterMenuTab'],
      flex: 1,
      valueFormatter: (params) => {
        if (params.data) {
          return context.enumService.getEnumTranslation(
            CASCADE_ENUM.TECHNOLOGY,
            params.data.TECHNOLOGY,
          );
        }
      },
    },
    {
      field: COLUMN_FIELDS.PROJECT_NAME,
      headerName: 'Project',
      cellRenderer: HyperlinkStaticCellRenderer,
      cellRendererParams: (params) => {
        const hyperlinkParams: IGridHyperlink = {
          value: params.data.NAME,
          id: params.data.PROJECT_ID,
          vppaId: null,
          iconList: null,
        };
        return hyperlinkParams;
      },
      sortable: true,
      filter: 'agTextColumnFilter',
      lockPinned: true,
      menuTabs: ['filterMenuTab'],
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.ISO,
      headerName: 'ISO',
      sortable: true,
      filter: 'agSetColumnFilter',
      lockPinned: true,
      menuTabs: ['filterMenuTab'],
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.SELLER_SETTLEMENT_POINT,
      headerName: 'Settlement Point',
      sortable: true,
      filter: 'agSetColumnFilter',
      lockPinned: true,
      menuTabs: ['filterMenuTab'],
      flex: 1,
      filterValueGetter: (params) => getSettlementPoint(params),
      valueFormatter: (params) => getSettlementPoint(params),
       comparator: (valueA, valueB, nodeA, nodeB) =>
         compareVPPAValues(valueA, valueB, nodeA, nodeB, 'VPPA_SETTLEMENT_POINT', 'VPPA_SETTLEMENT_POINT'),
    },
    {
      field: COLUMN_FIELDS.OFFERED_CAPACITY,
      headerName: 'Offered Capacity (MW)',
      sortable: true,
      filter: 'agTextColumnFilter',
      lockPinned: true,
      menuTabs: ['filterMenuTab'],
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.FIXED_PRICE,
      headerName: 'Fixed Price',
      valueFormatter: (params) => getFormattedNumber(params.data.FIXED_PRICE, '', 2, '$'),
      sortable: true,
      filter: 'agTextColumnFilter',
      lockPinned: true,
      menuTabs: ['filterMenuTab'],
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.TENOR_START_DATE,
      headerName: 'Start Date',
      sortable: true,
      filter: 'agTextColumnFilter',
      lockPinned: true,
      menuTabs: ['filterMenuTab'],
      filterValueGetter: (params) => getFormattedDate(params.data.TENOR_START_DATE),
      valueFormatter: (params) => getFormattedDate(params.data.TENOR_START_DATE),
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.TENOR_MONTHS,
      headerName: 'Tenor (mo)',
      sortable: true,
      filter: 'agTextColumnFilter',
      lockPinned: true,
      menuTabs: ['filterMenuTab'],
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.APPLICABLE_REC_PROGRAM,
      headerName: 'Applicable REC Program',
      sortable: true,
      filter: 'agSetColumnFilter',
      lockPinned: true,
      filterValueGetter: (params) => getApplicableRecProgram(context, params),
      menuTabs: ['filterMenuTab'],
      flex: 1,
      valueFormatter: (params) => getApplicableRecProgram(context, params),
    },
    {
      field: COLUMN_FIELDS.BUYER_POST_COD_COLLATERAL,
      headerName: 'Buyer Post-COD Credit $/MW (Max)',
      valueFormatter: (params) => {
        return getNumericPattern(
          params.data.BUYER_POST_COD_COLLATERAL,
          getAbbreviationForCreditProfile(params.data.RFQ_CREDIT_PROFILE),
          '$',
        );
      },
      sortable: false,
      filter: 'agTextColumnFilter',
      lockPinned: true,
      menuTabs: ['filterMenuTab'],
      flex: 1,
      ...headerWithTooltipColParams(CUSTOM_HEADER_TOOLTIPS.CREDIT_PROFILE),
    },
    {
      field: COLUMN_FIELDS.SELLER_POST_COD_COLLATERAL,
      headerName: 'Seller Post-COD Credit $/MW (Max)',
      valueFormatter: (params) =>
        getNumericPattern(
          params.data.SELLER_POST_COD_COLLATERAL,
          getAbbreviationForCreditProfile(params.data.VPPA_CREDIT_PROFILE),
          '$',
        ),
      sortable: false,
      filter: 'agTextColumnFilter',
      lockPinned: true,
      menuTabs: ['filterMenuTab'],
      flex: 1,
      ...headerWithTooltipColParams(CUSTOM_HEADER_TOOLTIPS.CREDIT_PROFILE),
    },
    {
      field: COLUMN_FIELDS.EXECUTED_DATE_TIME,
      headerName: 'Executed Date Time',
      sortable: true,
      filter: 'agTextColumnFilter',
      lockPinned: true,
      menuTabs: ['filterMenuTab'],
      filterValueGetter: (params) => getFormattedDate(params.data.EXECUTED_DATE_TIME),
      valueFormatter: (params) => {
        if (!params.data.EXECUTED_DATE_TIME) {
          return 'N/A';
        }
        return convertDateToUTC(new Date(params.data.EXECUTED_DATE_TIME)).toLocaleString();
      },
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.NEGOTIATION_STATUS,
      headerName: 'Negotiation Status',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      width: 205,
      pinned: 'right',
      lockPinned: true,
      suppressColumnsToolPanel: true,
      cellStyle: {
        'padding-right': '7px',
        'box-shadow': '-5px 0px 15px 0px rgba(0, 0, 0, 0.2)',
      },
      cellRenderer: ActionCellRenderer,
      cellRendererParams: (params) => {
        if (
          params.data.NEGOTIATION_STATUS == NEGOTIATION_STATUS.PENDING_EXECUTION ||
          params.data.NEGOTIATION_STATUS == NEGOTIATION_STATUS.EXECUTED_AWAITING_BUYER ||
          params.data.NEGOTIATION_STATUS == NEGOTIATION_STATUS.EXECUTED_AWAITING_SELLER ||
          params.data.NEGOTIATION_STATUS == NEGOTIATION_STATUS.EXECUTED
        ) {
          let name = '';
          let emitText = '';
          let action = '';
          let actionType = '';
          let tooltip = '';
          const isEnabled = true;

          switch (params.data.NEGOTIATION_STATUS) {
            case 'PENDING_EXECUTION': {
              actionType = ActionTypes.REVIEW;
              name = 'Pending Execution';
              emitText = 'goToExecution';
              action = 'goToExecution';
              tooltip = 'Go To Execution';
              break;
            }
            case 'EXECUTED_AWAITING_BUYER': {
              actionType = ActionTypes.REVIEW;
              name = 'Pending Buyer Execution';
              emitText = 'goToExecution';
              action = 'goToExecution';
              tooltip = 'Go To Execution';
              break;
            }
            case 'EXECUTED_AWAITING_SELLER': {
              actionType = ActionTypes.REVIEW;
              name = 'Pending Seller Execution';
              emitText = 'goToExecution';
              action = 'goToExecution';
              tooltip = 'Go To Execution';
              break;
            }
            case 'EXECUTED': {
              actionType = ActionTypes.SUCCESSFUL;
              name = 'Executed';
              if (
                params.data.RFQ_CONTRACT_TYPE === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED
              ) {
                emitText = 'goToExecution';
                action = 'goToExecution';
                tooltip = 'Go To Execution';
              } else {
                emitText = 'goToArteriaNegotiationsPage';
                action = 'goToArteriaNegotiationsPage';
                tooltip = 'Go to Contracting Module';
              }
              break;
            }
          }

          const reviewBtn = {
            name: name,
            callback: (detail) => {
              context.$emit(emitText, detail);
            },
            type: actionType,
            actionName: action,
            tooltip: tooltip,
          };

          const actions = [reviewBtn];
          return {
            actions,
            enabled: isEnabled,
          };
        } else if (
          params.data.NEGOTIATION_STATUS == NEGOTIATION_STATUS.IN_PROGRESS_ARTERIA ||
          params.data.NEGOTIATION_STATUS == NEGOTIATION_STATUS.REVIEW_EXECUTION_CHECKLIST ||
          params.data.NEGOTIATION_STATUS == NEGOTIATION_STATUS.READY_FOR_TRADE_EXECUTION ||
          params.data.NEGOTIATION_STATUS == NEGOTIATION_STATUS.EXPIRED ||
          params.data.NEGOTIATION_STATUS == NEGOTIATION_STATUS.REJECTED ||
          params.data.NEGOTIATION_STATUS == NEGOTIATION_STATUS.SELLER_DELETED ||
          params.data.NEGOTIATION_STATUS == NEGOTIATION_STATUS.BUYER_DELETED ||
          params.data.NEGOTIATION_STATUS == NEGOTIATION_STATUS.FULL_DELETED ||
          params.data.NEGOTIATION_STATUS == NEGOTIATION_STATUS.PENDING_EXECUTION_TIMER ||
          params.data.NEGOTIATION_STATUS == NEGOTIATION_STATUS.TRADED_AWAY
        ) {
          let name = '';
          let emitText = '';
          let action = '';
          let tooltip = '';
          let disabled = false;
          let actionType = ActionTypes.REVIEW;
          const isEnabled = true;

          switch (params.data.NEGOTIATION_STATUS) {
            case 'EXPIRED': {
              name = 'Ready to Execute';
              emitText = 'goToArteriaNegotiationsPage';
              action = 'goToArteriaNegotiationsPage';
              tooltip = 'Go to Contracting Module';
              break;
            }
            case 'REJECTED': {
              actionType = ActionTypes.REJECT;
              name = 'Rejected';
              emitText = '';
              action = '';
              break;
            }
            case 'TRADED_AWAY': {
              actionType = ActionTypes.REJECT;
              name = 'Traded Away';
              emitText = '';
              action = '';
              break;
            }
            case 'SELLER_DELETED': {
              name = 'Seller Deleted';
              emitText = '';
              action = '';
              break;
            }
            case 'BUYER_DELETED': {
              name = 'Buyer Deleted';
              emitText = '';
              action = '';
              break;
            }
            case 'FULL_DELETED': {
              name = 'Full Deleted';
              emitText = '';
              action = '';
              break;
            }
            case 'IN_PROGRESS_ARTERIA': {
              actionType = ActionTypes.IN_PROGRESS;
              name = 'In Progress';
              emitText = 'goToArteria';
              action = 'goToArteria';
              tooltip = 'Go to Contracting Module';
              break;
            }
            case 'REVIEW_EXECUTION_CHECKLIST': {
              actionType = ActionTypes.IN_PROGRESS;
              name = 'Review Execution Checklist';
              emitText = 'goToArteriaNegotiationsPage';
              action = 'goToArteriaNegotiationsPage';
              tooltip = 'Go to Contracting Module';
              break;
            }
            case 'READY_FOR_TRADE_EXECUTION': {
              name = 'Ready to Execute';
              emitText = 'goToArteriaNegotiationsPage';
              action = 'goToArteriaNegotiationsPage';
              tooltip = 'Go to Contracting Module';
              break;
            }
            case 'PENDING_EXECUTION_TIMER': {
              name = 'Ready to Execute';
              emitText = 'goToArteriaNegotiationsPage';
              action = 'goToArteriaNegotiationsPage';
              disabled = true;
              tooltip = 'The execution can only occur during Business Hour M-F 12AM-5PM EST';
              break;
            }
          }

          const button = {
            name: name,
            callback: (detail) => {
              context.$emit(emitText, detail);
            },
            type: actionType,
            actionName: action,
            disabled: disabled,
            tooltip: tooltip,
          };

          const actions = [button];
          return {
            actions,
            enabled: isEnabled,
          };
        }
      },
    },
  ];
}
